import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { findValue, FormikSubFieldProps, useFieldPlaceholder, useFormikFieldStyles } from './FormikField'
import { Field, Input, makeStyles, mergeClasses } from '@fluentui/react-components'
import { InputProps } from '@fluentui/react-input'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import * as UUID from 'uuid'
import { formatDecimals, normalizeTypedNumber } from '../../helpers/numberHelper'

export type FormikFormattableNumberInputFieldProps<Values> = FormikSubFieldProps<InputProps, Values> & {
    decimals?: number
    allowNegative?: boolean
    /** Where the number sits in the input. Amounts in a column of rates read better against the right edge. */
    align?: 'start' | 'end'
    /** Select the whole number when the field gets focus, so typing replaces it instead of adding to it. */
    selectOnFocus?: boolean
}

type FormatOptions = {
    decimals: number
    required?: boolean
    allowNegative?: boolean
}

const useStyles = makeStyles({
    // Without this the input keeps Fluent's intrinsic min-width and narrow columns scroll sideways.
    numberInput: { minWidth: 0 },
    alignEnd: { '> input': { textAlign: 'right' } },
})

/**
 * Rewrites what is typed into a number with `toSeparator` as decimal separator, so the user can type the separator of
 * their locale (12,5) while the form value stays in American notation (12.50) - and the other way around for display.
 * Only `strict` (leaving the field) pads the decimals, otherwise typing "12," would immediately become "12,00".
 */
export function formatToValue(value: string = '', options: FormatOptions, fromSeparator: string, toSeparator: string, strict: boolean): string {
    if ((!options.required && !value) || value === '') {
        return ''
    }

    //make sure the value is a string
    value = `${value}`

    const decimalLength = options.decimals

    //Only accept `fromSeparator` as decimal separator
    const decimalPosition = value.lastIndexOf(fromSeparator)

    let numberValue = value
    let decimalValue = ''
    if (decimalPosition >= 0) {
        numberValue = numberValue.substring(0, decimalPosition)
        decimalValue = value.substring(decimalPosition + 1)
    }

    //Parse numberValue
    numberValue = numberValue
        .replace(/[^\d-]/, '') // Only accept numbers and dashes.
        .replace(/^[0]+([\d]+)/, '$1') // Remove excess preceding 0

    if (!options.allowNegative) {
        numberValue = numberValue.replace('-', '')
    }
    if (numberValue === '') {
        numberValue = '0'
    }

    //Parse decimalValue
    if (strict) {
        decimalValue = (decimalValue || '').padEnd(decimalLength, '0')
    }
    if (decimalValue && decimalValue.length > decimalLength) {
        decimalValue = decimalValue.substring(0, decimalLength)
    }

    //Concat both values
    value = numberValue
    if (decimalValue || decimalPosition >= 0) {
        value = value + toSeparator
    }
    if (decimalValue) {
        value = value + decimalValue
    }

    return value
}

/**
 * The number field behind the money and distance fields: the user works with the Dutch comma while the form value
 * keeps the American notation the backend expects. While the field has focus it shows exactly what is being typed;
 * as soon as it is left the number is shown rounded to its decimals and with thousand separators, the way
 * FormattableNumberInput does - this field replaces the FormikField + FormattableNumberInput/MoneyInput combination.
 *
 * What differs per kind of number - the decimals, the unit or the currency symbol - is left to the fields that use it.
 */
export default function FormikFormattableNumberInputField<Values>(props: FormikFormattableNumberInputFieldProps<Values>): ReactElement {
    const styles = useStyles()
    const formikFieldStyles = useFormikFieldStyles()
    const { t: translate } = useTranslation()

    const { values, errors, setFieldValue } = useFormikContext<Values>()

    const [focused, setFocused] = useState<boolean>(false)
    const [itemRef] = useState<string>(UUID.v4())

    const options = useMemo<FormatOptions>(
        () => ({ decimals: props.decimals ?? 2, required: props.required, allowNegative: props.allowNegative }),
        [props.decimals, props.required, props.allowNegative],
    )

    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [values, errors])
    const validationState = useMemo(() => (findValue(errors, props.fieldName as string) ? 'error' : 'none'), [values, errors])

    const currentValue = useMemo(() => findValue(values, props.fieldName as string), [values, props.fieldName])
    const currentDisplayValue = useMemo(() => {
        const typedValue = formatToValue(currentValue, options, '.', ',', false) || ''
        // An empty field stays empty; only a number is shown the way it is read, not typed.
        if (focused || typedValue === '') {
            return typedValue
        }
        return formatDecimals(currentValue, options.decimals, 'en', 'nl-nl', true)
    }, [currentValue, options, focused])

    const componentProps = props.componentProps as InputProps
    const placeholderValue = useFieldPlaceholder(props)

    // The number specific props are ours, they are no part of the Field below.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { decimals: _decimals, allowNegative: _allowNegative, align: _align, selectOnFocus: _selectOnFocus, ...fieldProps } = props

    const commit = (value: string, strict: boolean) => setFieldValue(props.fieldName as string, formatToValue(value, options, ',', '.', strict))

    return (
        <Field
            label={props.renderLabel ? props.renderLabel : props.labelKey ? translate(props.labelKey, { ...props.labelKeyProps }) : undefined}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey, { ...props.hintKeyProps }) : undefined)}
            {...fieldProps}
            validationMessage={validationMessage}
            validationState={validationState}
            className={props.className ?? (!props.renderLabel && !props.labelKey && formikFieldStyles.noLabel)}
        >
            <Input
                itemRef={itemRef}
                required={props.required}
                value={currentDisplayValue}
                autoComplete="off"
                data-lpignore={'true'}
                {...componentProps}
                className={mergeClasses(styles.numberInput, props.align === 'end' && styles.alignEnd, componentProps?.className)}
                {...(placeholderValue ? { placeholder: placeholderValue } : {})}
                onChange={(event, data) => {
                    commit(normalizeTypedNumber(data.value, props.allowNegative), false)
                    componentProps?.onChange?.(event, data)
                }}
                onFocus={event => {
                    setFocused(true)
                    if (props.selectOnFocus) {
                        event.target.select()
                    }
                    componentProps?.onFocus?.(event)
                }}
                onBlur={event => {
                    setFocused(false)
                    commit(event.currentTarget.value, true)
                    componentProps?.onBlur?.(event)
                }}
                onKeyDown={event => {
                    if (event.key === 'Enter') {
                        commit(event.currentTarget.value, true)
                    }
                    componentProps?.onKeyDown?.(event)
                }}
            />
        </Field>
    )
}
